/**
 * RemoveCrewReasonDialog — "Gỡ … khỏi buổi?" (QĐ 0424, CEO duyệt mẫu 5a và
 * chốt 6a ngày 19/10/2026).
 *
 * Bấm × gỡ thợ chụp / trang điểm ở màn Lịch hoặc Đơn phải chọn một trong sáu
 * lý do của nút "Đổi người" ở Bảng suất. Hai lý do cuối ghi phiếu lỗi cho
 * người bị gỡ — người bấm đọc được điều đó NGAY trên dòng lựa chọn. Nhắc luôn
 * 2 phút chờ của QĐ 0423: gỡ xong máy để trống chỗ cho người bấm chọn người
 * thay.
 */
import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Button } from "@/ui/button";
import { Label } from "@/ui/label";
import { Textarea } from "@/ui/textarea";
import {
  OVERRIDE_REASONS,
  isOverrideReasonCode,
  type OverrideReasonCode,
} from "@/features/order-dispatch/overrideReasons";

/** Trần ô ghi thêm — khớp crewRemovalNoteMax của máy chủ. */
export const REMOVE_NOTE_MAX = 500;

type Props = {
  /** Tên người sắp gỡ; null = hộp đóng. */
  personName: string | null;
  /** Dòng phụ: vai · giờ · ngày · khách. */
  context: string;
  pending: boolean;
  onConfirm: (code: OverrideReasonCode, note: string) => void;
  onOpenChange: (open: boolean) => void;
};

export function RemoveCrewReasonDialog({ personName, context, pending, onConfirm, onOpenChange }: Props) {
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");

  // Xoá lựa chọn ngay lúc đóng: lý do của lần gỡ trước còn dính lại là lần
  // sau bấm nhầm một phát ghi lỗi cho người khác.
  function change(open: boolean) {
    if (!open) {
      setCode("");
      setNote("");
    }
    onOpenChange(open);
  }

  const name = personName ?? "";
  const noFault = OVERRIDE_REASONS.filter((r) => !r.fault);
  const fault = OVERRIDE_REASONS.filter((r) => r.fault);

  return (
    <Dialog open={personName !== null} onOpenChange={change}>
      {/* content-start: hộp ngắn trên điện thoại không bị lưới 3 hàng kéo giãn. */}
      <DialogContent className="max-w-md content-start">
        <DialogHeader>
          <DialogTitle>Gỡ {name} khỏi buổi?</DialogTitle>
          <DialogDescription>{context}</DialogDescription>
        </DialogHeader>

        <fieldset className="space-y-2" disabled={pending}>
          <legend className="mb-1.5 text-sm font-semibold">Gỡ vì lý do gì? (bắt buộc)</legend>
          {noFault.map((r) => (
            <ReasonOption key={r.code} code={r.code} label={r.label} checked={code === r.code} onPick={setCode} />
          ))}
          <p className="px-0.5 pt-1 text-xs text-muted-foreground">Hai lý do dưới ghi phiếu lỗi cho người bị gỡ:</p>
          {fault.map((r) => (
            <ReasonOption
              key={r.code}
              code={r.code}
              label={r.label}
              hint={`Ghi phiếu lỗi cho ${name}`}
              checked={code === r.code}
              onPick={setCode}
            />
          ))}
        </fieldset>

        <div className="space-y-1.5">
          <Label htmlFor="remove-crew-note">Ghi thêm (không bắt buộc)</Label>
          <Textarea
            id="remove-crew-note"
            value={note}
            maxLength={REMOVE_NOTE_MAX}
            disabled={pending}
            placeholder="VD: ca trước trễ 40 phút"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <p className="rounded-md bg-[var(--color-info-bg)] px-3 py-2 text-xs text-[var(--color-info-text)]">
          Gỡ xong bạn có <b>2 phút</b> để bấm “+ Thêm người” chọn người thay. Quá 2 phút chưa thêm ai thì máy tự
          xếp người khác. Người bạn gỡ sẽ không được máy xếp lại vào buổi này.
        </p>

        <DialogFooter>
          <Button variant="ghost" onClick={() => change(false)}>
            Huỷ
          </Button>
          <Button
            disabled={pending || !isOverrideReasonCode(code)}
            onClick={() => {
              if (!isOverrideReasonCode(code)) return;
              onConfirm(code, note);
            }}
          >
            Gỡ người
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReasonOption({
  code,
  label,
  hint,
  checked,
  onPick,
}: {
  code: string;
  label: string;
  hint?: string;
  checked: boolean;
  onPick: (code: string) => void;
}) {
  return (
    <label
      className={`flex min-h-[var(--touch-min,44px)] cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm ${
        checked ? "border-primary bg-primary/5" : "border-border"
      }`}
    >
      <input
        type="radio"
        name="remove-crew-reason"
        value={code}
        checked={checked}
        onChange={() => onPick(code)}
        className="mt-0.5"
      />
      <span>
        {label}
        {hint ? <span className="mt-0.5 block text-xs text-[var(--color-warning-text)]">{hint}</span> : null}
      </span>
    </label>
  );
}
